import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Server,
  Database,
  Cloud,
  Lock,
  Zap as Lightning,
  Network,
  CheckCircle,
  ArrowRight,
  Check,
  X,
  GitBranch,
  Activity,
  Plug,
  BarChart3,
  Bell,
  Link2,
  Settings,
} from "lucide-react";
import "./APIandBackend.css";
import { subscribeToNewsletter } from "./newsletterService";

export default function APIBackendServices() {
  const [activeTab, setActiveTab] = useState("backend");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fallbackPalette = ["#ea580c", "#f97316", "#ef4444", "#f59e0b", "#0ea5e9", "#10b981"];

  const getInitials = (name) =>
    name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const makeFallbackSvg = (label, color) => {
    const safeLabel = getInitials(label);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.9" />
            <stop offset="100%" stop-color="${color}" stop-opacity="0.7" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="56" height="56" rx="14" fill="url(#g)" />
        <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#ffffff">
          ${safeLabel}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const startProject = () => {
    window.location.href = "/#contact";
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) {
      form.reset();
    }
  };

  const technologies = {
    backend: [
      {
        name: "Node.js",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
        color: "#339933",
      },
      {
        name: "Python",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
        color: "#3776AB",
      },
      {
        name: "PHP",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg",
        color: "#777BB4",
      },
      {
        name: "Go",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg",
        color: "#00ADD8",
      },
      {
        name: "Ruby",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ruby/ruby-original.svg",
        color: "#CC342D",
      },
      {
        name: "Java",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
        color: "#007396",
      },
    ],
    databases: [
      {
        name: "PostgreSQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
        color: "#4169E1",
      },
      {
        name: "MongoDB",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
        color: "#47A248",
      },
      {
        name: "MySQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg",
        color: "#4479A1",
      },
      {
        name: "Redis",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
        color: "#DC382D",
      },
      {
        name: "Elasticsearch",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/elasticsearch/elasticsearch-original.svg",
        color: "#005571",
      },
      {
        name: "Cassandra",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cassandra/cassandra-original.svg",
        color: "#1287B1",
      },
    ],
    cloud: [
      {
        name: "AWS",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
        color: "#FF9900",
      },
      {
        name: "Google Cloud",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
        color: "#4285F4",
      },
      {
        name: "Azure",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
        color: "#0078D4",
      },
      {
        name: "Docker",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
        color: "#2496ED",
      },
      {
        name: "Kubernetes",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
        color: "#326CE5",
      },
      {
        name: "Nginx",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nginx/nginx-original.svg",
        color: "#009639",
      },
    ],
  };

  const whatWeOffer = [
    {
      icon: <Server className="w-6 h-6" />,
      title: "RESTful API Development",
      description:
        "Build robust, scalable REST APIs with proper documentation, versioning, and authentication systems.",
    },
    {
      icon: <GitBranch className="w-6 h-6" />,
      title: "GraphQL APIs",
      description:
        "Flexible, efficient GraphQL APIs that let clients request exactly the data they need.",
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Database Design",
      description:
        "Optimized database architecture for performance, scalability, and data integrity.",
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud Infrastructure",
      description:
        "Set up and manage cloud infrastructure on AWS, Google Cloud, or Azure for optimal performance.",
    },
    {
      icon: <Network className="w-6 h-6" />,
      title: "Microservices Architecture",
      description:
        "Design and implement microservices for scalable, maintainable distributed systems.",
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "API Security",
      description:
        "Implement authentication, authorization, rate limiting, and security best practices.",
    },
  ];

  const whyChooseUs = [
    "Scalable architecture design",
    "High-performance optimization",
    "Comprehensive API documentation",
    "Security-first approach",
    "Real-time data processing",
    "Automated testing and CI/CD",
    "Load balancing and caching",
    "24/7 monitoring and support",
  ];

  const packages = [
    {
      name: "Basic API",
      price: "From R8,000",
      ideal: "Simple data APIs",
      features: [
        { text: "RESTful API development", included: true },
        { text: "Basic authentication", included: true },
        { text: "API documentation", included: true },
        { text: "Single database", included: true },
        { text: "Basic error handling", included: true },
        { text: "GraphQL support", included: false },
        { text: "Advanced security", included: false },
        { text: "Microservices", included: false },
      ],
      popular: false,
    },
    {
      name: "Professional",
      price: "From R25,000",
      ideal: "Production-ready systems",
      features: [
        { text: "REST or GraphQL APIs", included: true },
        { text: "OAuth 2.0 / JWT auth", included: true },
        { text: "Interactive documentation", included: true },
        { text: "Multiple databases", included: true },
        { text: "Error tracking & logging", included: true },
        { text: "Rate limiting", included: true },
        { text: "Caching layer", included: true },
        { text: "Load balancing", included: true },
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "From R60,000",
      ideal: "Large-scale infrastructure",
      features: [
        { text: "Microservices architecture", included: true },
        { text: "Advanced security suite", included: true },
        { text: "Full API gateway", included: true },
        { text: "Distributed databases", included: true },
        { text: "Real-time monitoring", included: true },
        { text: "Auto-scaling setup", included: true },
        { text: "CDN integration", included: true },
        { text: "DevOps & deployment", included: true },
      ],
      popular: false,
    },
  ];

  const process = [
    {
      step: "1",
      title: "Requirements & Planning",
      description:
        "Define API endpoints, data models, authentication needs, and performance requirements.",
    },
    {
      step: "2",
      title: "Architecture Design",
      description:
        "Design system architecture, database schema, and API structure for scalability.",
    },
    {
      step: "3",
      title: "Development",
      description:
        "Build APIs with clean code, proper error handling, and comprehensive testing.",
    },
    {
      step: "4",
      title: "Testing & Security",
      description:
        "Unit tests, integration tests, load testing, and security audits.",
    },
    {
      step: "5",
      title: "Deployment & Monitoring",
      description:
        "Deploy to production with monitoring, logging, and performance tracking.",
    },
  ];

  const apiTypes = [
    {
      name: "REST APIs",
      icon: <Plug className="w-8 h-8" />,
      description: "Standard HTTP-based APIs for web and mobile applications",
    },
    {
      name: "GraphQL APIs",
      icon: <BarChart3 className="w-8 h-8" />,
      description: "Flexible query language for efficient data fetching",
    },
    {
      name: "WebSocket APIs",
      icon: <Lightning className="w-8 h-8" />,
      description: "Real-time bi-directional communication for live updates",
    },
    {
      name: "Webhook Systems",
      icon: <Bell className="w-8 h-8" />,
      description: "Event-driven notifications and automated integrations",
    },
    {
      name: "Third-party Integration",
      icon: <Link2 className="w-8 h-8" />,
      description: "Connect with payment gateways, CRMs, and external services",
    },
    {
      name: "Data Processing",
      icon: <Settings className="w-8 h-8" />,
      description: "Background jobs, queues, and batch processing systems",
    },
  ];

  const apiTypesPreview = apiTypes.slice(0, 4);
  const apiTypesMore = apiTypes.slice(4);

  const features = [
    {
      title: "Performance",
      description:
        "Optimized for speed with caching, CDN integration, and efficient database queries.",
      icon: <Lightning className="w-8 h-8" />,
    },
    {
      title: "Scalability",
      description:
        "Built to handle growing traffic with horizontal scaling and load balancing.",
      icon: <Activity className="w-8 h-8" />,
    },
    {
      title: "Security",
      description:
        "Enterprise-grade security with encryption, authentication, and rate limiting.",
      icon: <Lock className="w-8 h-8" />,
    },
    {
      title: "Reliability",
      description:
        "99.9% uptime with redundancy, failover systems, and automated backups.",
      icon: <Server className="w-8 h-8" />,
    },
  ];

  return (
    <div className="api-backend-page">
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
              <div className="container navbar-container">
                <a href="/" className="navbar-brand">
                  <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
                </a>
      
                <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
                  <div className="services-dropdown">
                    <div className="navbar-link active-link">Services</div>
                    <div className="dropdown-menu">
                      <Link to="/web-development" className="dropdown-item">
                        <i className="fas fa-laptop-code"></i>
                        Web Development Services
                      </Link>
                      <Link to="/mobile-app" className="dropdown-item">
                        <i className="fas fa-mobile-alt"></i>
                        Mobile App Development
                      </Link>
                      <Link to="/custom-software" className="dropdown-item">
                        <i className="fas fa-cogs"></i>
                        Custom Software Development
                      </Link>
                      <Link to="/api-backend" className="dropdown-item active-link">
                        <i className="fas fa-plug"></i>
                        API & Backend Services
                      </Link>
                      <Link to="/maintenance" className="dropdown-item">
                        <i className="fas fa-tools"></i>
                        Website & App Maintenance
                      </Link>
                      <Link to="/ai-services" className="dropdown-item">
                        <i className="fas fa-robot"></i>
                        AI & Machine Learning Services
                      </Link>
                    </div>
                  </div>
                  {/* <a href="/#work" className="navbar-link">Work</a> */}
                  <a href="/#about" className="navbar-link">
                    About
                  </a>
{/*
                  <a href="/#testimonials" className="navbar-link">
                    Testimonials
                  </a>
*/}
                  <a href="/#faq" className="navbar-link">
                    FAQ
                  </a>
                  <a href="/#contact" className="navbar-link">
                    Contact
                  </a>
                  <Link to="/track-project" className="navbar-link">
                    Track Project
                  </Link>
                  <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
                    Request Consultation
                  </button>
                </div>
      
                <button
                  className="navbar-toggle"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
                </button>
              </div>
            </nav>

      {/* Hero Section */}
      <section className="api-backend-hero">
        <div className="api-backend-container">
          <div className="api-backend-breadcrumb">
            <Link to="/">Home</Link> / <Link to="/#services">Services</Link> /{" "}
            <span>API & Backend Services</span>
          </div>
          <h1 className="api-backend-hero-title">API & Backend Services</h1>
          <p className="api-backend-hero-subtitle">
            Power your applications with robust, scalable backend infrastructure
            and APIs designed for performance, security, and reliability for
            Johannesburg and South African teams.
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="api-backend-overview">
        <div className="api-backend-container">
          <div className="api-backend-grid-2col">
            <div>
              <h2 className="api-backend-section-title">
                The Core Platform Behind Reliable Applications
              </h2>
              <p className="api-backend-section-subtitle">
                Every successful application needs a powerful backend. We build
                the server-side infrastructure, APIs, and databases that power
                your web and mobile applications, ensuring they&apos;re fast,
                secure, and ready to scale for businesses across Johannesburg.
              </p>
              <p className="api-backend-section-subtitle">
                From simple REST APIs to complex microservices architectures,
                our backend solutions follow proven engineering standards so
                your applications can support current demand and future growth.
              </p>
              <div className="api-backend-highlight-box">
                <h3 className="api-backend-highlight-title">What We Deliver</h3>
                <div className="api-backend-highlight-list">
                  {whyChooseUs.slice(0, 4).map((benefit, idx) => (
                    <div key={idx} className="api-backend-highlight-item">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="api-backend-highlight-text">
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="api-backend-image-container">
              <img
                src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=80"
                alt="Backend Development"
                className="api-backend-image"
              />
              <div className="api-backend-stat-card">
                <div className="api-backend-stat-content">
                  <Activity className="api-backend-stat-icon" />
                  <div>
                    <div className="api-backend-stat-value">99.9%</div>
                    <div className="api-backend-stat-label">
                      Uptime Guarantee
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="api-backend-features">
        <div className="api-backend-container">
          <h2 className="api-backend-features-title">
            Built for Performance & Scale
          </h2>
          <div className="api-backend-features-grid">
            {features.map((feature, idx) => (
              <div key={idx} className="api-backend-feature-card">
                <div className="api-backend-feature-icon">{feature.icon}</div>
                <h3 className="api-backend-feature-title">{feature.title}</h3>
                <p className="api-backend-feature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="api-backend-services">
        <div className="api-backend-container">
          <h2 className="api-backend-services-title">Our Backend Services</h2>
          <p className="api-backend-services-subtitle">
            Enterprise-grade backend engineering and API integration services
          </p>
          <div className="api-backend-services-grid">
            {whatWeOffer.map((item, idx) => (
              <div key={idx} className="api-backend-service-card">
                <div className="api-backend-service-icon">{item.icon}</div>
                <h3 className="api-backend-service-title">{item.title}</h3>
                <p className="api-backend-service-description">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Types Section */}
      <section className="api-backend-types">
        <div className="api-backend-container">
          <div className="api-backend-types-content">
            <div className="api-backend-image-container">
              <img
                src="https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=1400&q=80"
                alt="API Development"
                className="api-backend-image"
              />
              <div className="api-backend-types-badge">
                <div className="api-backend-types-badge-content">
                  <Lightning className="w-8 h-8" />
                  <div>
                    <div className="api-backend-stat-value">&lt; 100ms</div>
                    <div className="api-backend-stat-label">
                      Average Response Time
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="api-backend-section-title">
                API Solutions We Build
              </h2>
              <p className="api-backend-section-subtitle">
                From REST to real-time, we build APIs that power modern
                applications with speed, security, and reliability.
              </p>
              <div className="api-backend-grid-2col">
                {apiTypesPreview.map((api, idx) => (
                  <div key={idx} className="api-backend-type-card">
                    <div className="api-backend-type-icon">{api.icon}</div>
                    <h3 className="api-backend-type-title">{api.name}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {apiTypesMore.length > 0 && (
            <div className="api-backend-types-grid">
              {apiTypesMore.map((api, idx) => (
                <div key={idx} className="api-backend-type-card">
                  <div className="api-backend-type-icon">{api.icon}</div>
                  <h3 className="api-backend-type-title">{api.name}</h3>
                  <p className="api-backend-type-description">
                    {api.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="api-backend-technologies">
        <div className="api-backend-container">
          <div className="api-backend-technologies-header">
            <h2 className="api-backend-technologies-title">
              Our Technology Stack
            </h2>
            <p className="api-backend-technologies-subtitle">
              Industry-leading backend technologies and infrastructure
            </p>
          </div>

          <div className="api-backend-tech-categories">
            <div className="api-backend-category-card">
              <img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80"
                alt="Cloud Infrastructure"
                className="api-backend-category-image"
              />
              <div className="api-backend-category-overlay">
                <div className="api-backend-category-content">
                  <h3 className="api-backend-category-name">
                    Cloud Infrastructure
                  </h3>
                  <p className="api-backend-category-description">
                    AWS, Google Cloud, Azure
                  </p>
                </div>
              </div>
            </div>

            <div className="api-backend-category-card">
              <img
                src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=80"
                alt="Database Solutions"
                className="api-backend-category-image"
              />
              <div className="api-backend-category-overlay">
                <div className="api-backend-category-content">
                  <h3 className="api-backend-category-name">
                    Database Solutions
                  </h3>
                  <p className="api-backend-category-description">
                    SQL & NoSQL databases
                  </p>
                </div>
              </div>
            </div>

            <div className="api-backend-category-card">
              <img
                src="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&w=1200&q=80"
                alt="DevOps & Automation"
                className="api-backend-category-image"
              />
              <div className="api-backend-category-overlay">
                <div className="api-backend-category-content">
                  <h3 className="api-backend-category-name">
                    DevOps & Automation
                  </h3>
                  <p className="api-backend-category-description">
                    CI/CD, Docker, Kubernetes
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="api-backend-tab-container">
            {Object.keys(technologies).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`api-backend-tab-btn ${
                  activeTab === tab ? "active" : ""
                }`}
              >
                {tab === "backend"
                  ? "Backend Frameworks"
                  : tab === "databases"
                  ? "Databases"
                  : "Cloud & DevOps"}
              </button>
            ))}
          </div>

          <div className="api-backend-technologies-grid">
            {technologies[activeTab].map((tech, idx) => (
              <div key={idx} className="api-backend-tech-card">
                <div className="api-backend-tech-icon">
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      const color =
                        tech.color ||
                        fallbackPalette[
                          Math.abs(tech.name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) %
                            fallbackPalette.length
                        ];
                      e.target.src = makeFallbackSvg(tech.name, color);
                    }}
                  />
                </div>
                <span className="api-backend-tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="api-backend-process">
        <div className="api-backend-container">
          <h2 className="api-backend-process-title">Our Development Process</h2>
          <p className="api-backend-process-subtitle">
            From planning to deployment, we manage backend delivery end-to-end
          </p>
          <div className="api-backend-process-steps">
            {process.map((item, idx) => (
              <div key={idx} className="api-backend-process-step">
                <div className="api-backend-step-number">{item.step}</div>
                <h3 className="api-backend-step-title">{item.title}</h3>
                <p className="api-backend-step-description">
                  {item.description}
                </p>
                {idx < process.length - 1 && (
                  <div className="api-backend-process-arrow">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="api-backend-packages">
        <div className="api-backend-container">
          <h2 className="api-backend-packages-title">Choose Your Package</h2>
          <p className="api-backend-packages-subtitle">
            Scalable backend solutions for every project size
          </p>
          <div className="api-backend-packages-grid">
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`api-backend-package-card ${
                  pkg.popular ? "popular" : "standard"
                }`}
              >
                {pkg.popular && (
                  <div className="api-backend-popular-badge">MOST POPULAR</div>
                )}
                <h3 className="api-backend-package-name">{pkg.name}</h3>
                <div
                  className={`api-backend-package-price ${
                    pkg.popular ? "popular" : "standard"
                  }`}
                >
                  {pkg.price}
                </div>
                <p
                  className={`api-backend-package-ideal ${
                    pkg.popular ? "popular" : "standard"
                  }`}
                >
                  {pkg.ideal}
                </p>

                <div className="api-backend-features-list">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="api-backend-feature-item">
                      {feature.included ? (
                        <Check className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 flex-shrink-0" />
                      )}
                      <span
                        className={`api-backend-feature-text ${
                          feature.included ? "included" : "excluded"
                        } ${pkg.popular ? "popular" : "standard"}`}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  className={`api-backend-package-btn ${
                    pkg.popular ? "popular" : "standard"
                  }`}
                  onClick={startProject}
                >
                  Discuss Requirements
                </button>
              </div>
            ))}
          </div>
          <p className="api-backend-packages-note">
            All packages include comprehensive API documentation and 90 days of
            support
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="api-backend-cta">
        <div className="api-backend-container">
          <h2 className="api-backend-cta-title">
            Plan Your Next Backend Initiative
          </h2>
          <p className="api-backend-cta-subtitle">
            Discuss your roadmap with our team and get a clear delivery plan
            for secure, scalable backend systems.
          </p>
          <div className="api-backend-cta-buttons">
            <button className="api-backend-cta-btn primary" onClick={startProject}>
              Request Consultation <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems for
                organizations that require reliability, scalability, and strong
                delivery governance.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href="https://www.instagram.com/techxm_za/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.facebook.com/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="/web-development">
                    <i className="fas fa-chevron-right"></i>
                    Web Development
                  </a>
                </li>
                <li>
                  <a href="/mobile-app">
                    <i className="fas fa-chevron-right"></i>
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="/custom-software">
                    <i className="fas fa-chevron-right"></i>
                    Custom Software
                  </a>
                </li>
                <li>
                  <a href="/api-backend">
                    <i className="fas fa-chevron-right"></i>
                    API & Backend
                  </a>
                </li>
                <li>
                  <a href="/maintenance">
                    <i className="fas fa-chevron-right"></i>
                    Maintenance
                  </a>
                </li>
                <li>
                  <a href="/ai-services">
                    <i className="fas fa-chevron-right"></i>
                    AI & Machine Learning
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/#about">
                    <i className="fas fa-chevron-right"></i>
                    About
                  </a>
                </li>
                <li>
                  <a href="/#faq">
                    <i className="fas fa-chevron-right"></i>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/track-project">
                    <i className="fas fa-chevron-right"></i>
                    Track Project
                  </a>
                </li>
                <li>
{/*
                  <a href="/#testimonials">
                    <i className="fas fa-chevron-right"></i>
                    Testimonials
                  </a>
*/}
                </li>
                <li>
                  <a href="/#contact">
                    <i className="fas fa-chevron-right"></i>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ohenimuri, Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; 2025 TECHXM. All rights reserved. Enterprise Digital
              Solutions.
            </p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}








